/** Per-user choice between icons with labels and icons alone on the main rail.
 *
 *  Read and written through the Preferences store (navigation.labelMode). It is also dual-written
 *  to the legacy user preferences key `main_rail_label_mode` for clients that predate the store,
 *  and that key seeds the store once when the store still holds the default. The localStorage copy
 *  is a first-paint cache so the rail never flashes the other width. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePreferences } from '../../features/preferences/PreferencesContext';
import { API_BASE, getAuthHeaders } from '../../utils/supabase/client';

export type MainRailLabelMode = 'icons-and-text' | 'icons-only';
export const DEFAULT_MAIN_RAIL_LABEL_MODE: MainRailLabelMode = 'icons-and-text';

const PREFERENCE_KEY = 'main_rail_label_mode';

export const mainRailLabelModeCacheKey = (userId: string) => `quantis:main-rail-label-mode:v1:${userId}`;

export function sanitiseLabelMode(value: unknown): MainRailLabelMode {
  return value === 'icons-only' ? 'icons-only' : DEFAULT_MAIN_RAIL_LABEL_MODE;
}

function readCache(userId: string): MainRailLabelMode {
  if (!userId) return DEFAULT_MAIN_RAIL_LABEL_MODE;
  try {
    return sanitiseLabelMode(localStorage.getItem(mainRailLabelModeCacheKey(userId)));
  } catch {
    return DEFAULT_MAIN_RAIL_LABEL_MODE;
  }
}

function writeCache(userId: string, mode: MainRailLabelMode): void {
  if (!userId) return;
  try {
    if (mode === DEFAULT_MAIN_RAIL_LABEL_MODE) localStorage.removeItem(mainRailLabelModeCacheKey(userId));
    else localStorage.setItem(mainRailLabelModeCacheKey(userId), mode);
  } catch { /* the cache is an optimisation, never a requirement */ }
}

const endpoint = (userId: string) => `${API_BASE}/users/${userId}/preferences/${PREFERENCE_KEY}`;

// A later choice must never be overtaken by an earlier save or read.
const saveQueues = new Map<string, Promise<unknown>>();
const changeCounts = new Map<string, number>();
const seededUsers = new Set<string>();

async function saveToServer(userId: string, mode: MainRailLabelMode): Promise<void> {
  const res = await fetch(endpoint(userId), {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ value: mode }),
  });
  if (!res.ok && res.status !== 404) throw new Error(`Failed to save rail label mode (${res.status})`);
}

async function readLegacyMode(userId: string): Promise<MainRailLabelMode | null> {
  await (saveQueues.get(userId) ?? Promise.resolve());
  const res = await fetch(endpoint(userId), { headers: await getAuthHeaders() });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.value === 'icons-only' || data?.value === 'icons-and-text' ? sanitiseLabelMode(data.value) : null;
}

export function useMainRailLabelMode(userId: string) {
  const { preferences, loaded, update } = usePreferences();
  const storedMode = preferences.navigation.labelMode;
  const [touched, setTouched] = useState(false);
  const legacyCache = useMemo(() => readCache(userId), [userId]);
  const storedModeRef = useRef(storedMode);
  storedModeRef.current = storedMode;

  const mode: MainRailLabelMode = loaded || touched || storedMode !== DEFAULT_MAIN_RAIL_LABEL_MODE
    ? sanitiseLabelMode(storedMode)
    : legacyCache;

  useEffect(() => {
    if (!userId || !loaded || seededUsers.has(userId)) return;
    seededUsers.add(userId);
    if (storedModeRef.current !== DEFAULT_MAIN_RAIL_LABEL_MODE) return;
    const startedAt = changeCounts.get(userId) ?? 0;
    void readLegacyMode(userId)
      .then((legacy) => {
        if (!legacy || legacy === DEFAULT_MAIN_RAIL_LABEL_MODE) return;
        if ((changeCounts.get(userId) ?? 0) !== startedAt || storedModeRef.current !== DEFAULT_MAIN_RAIL_LABEL_MODE) return;
        update({ navigation: { labelMode: legacy } });
      })
      .catch(() => { /* offline or dead session: the store value stands */ });
  }, [userId, loaded, update]);

  const setMode = useCallback((next: MainRailLabelMode) => {
    const value = sanitiseLabelMode(next);
    changeCounts.set(userId, (changeCounts.get(userId) ?? 0) + 1);
    setTouched(true);
    update({ navigation: { labelMode: value } });
    writeCache(userId, value);
    if (!userId) return;
    const queued = (saveQueues.get(userId) ?? Promise.resolve()).then(() => saveToServer(userId, value));
    saveQueues.set(userId, queued.catch(() => {}));
    queued.catch(() => { /* kept in the store */ });
  }, [userId, update]);

  return { mode, setMode };
}
